import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  IconButton,
  TextField,
  Button,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { FileCopy, Check, Close, Edit } from '@mui/icons-material';
import { convertNewlinesToBreaks, formatLetterDate } from '../../utils/utils';
// rename it inline. Mirrors the "letter on cream paper" look used on /create.
export default function LetterReaderModal({
  open,
  handleClose,
  letter,
  onRename,
  renaming,
}) {
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    setEditingName(false);
    setNameValue(letter?.name || '');
    // reset only when a different letter opens, not on external name edits
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [letter?.id]);

  if (!letter) return null;

  const handleCopy = () => {
    navigator.clipboard
      .writeText(letter.generatedLetter || '')
      .then(() => setCopied(true))
      .catch((err) => console.log('Failed to copy text: ', err));
  };

  const commitRename = async () => {
    const trimmed = nameValue.trim();
    if (trimmed && trimmed !== letter.name) {
      await onRename(letter.id, trimmed);
    }
    setEditingName(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          {editingName ? (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ flexGrow: 1 }}
            >
              <TextField
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && commitRename()}
                autoFocus
                fullWidth
                size="small"
                variant="standard"
                inputProps={{ maxLength: 120 }}
              />
              <Tooltip title="Save name">
                <span>
                  <IconButton
                    onClick={commitRename}
                    disabled={renaming}
                    color="primary"
                    size="small"
                  >
                    {renaming ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <Check />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton
                  onClick={() => {
                    setEditingName(false);
                    setNameValue(letter.name || '');
                  }}
                  size="small"
                >
                  <Close />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : (
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 'bold',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {letter.name}
                </Typography>
                <Tooltip title="Rename">
                  <IconButton
                    onClick={() => setEditingName(true)}
                    size="small"
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {formatLetterDate(letter.createdAt)}
              </Typography>
            </Box>
          )}
          <Tooltip title="Copy cover letter">
            <IconButton onClick={handleCopy}>
              <FileCopy />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Box
          sx={{
            backgroundColor: '#FDF6E3',
            color: '#333',
            borderRadius: 1,
            p: { xs: 2, sm: 4 },
            boxShadow: 'inset 0 0 8px rgba(0, 0, 0, 0.06)',
          }}
        >
          <Typography variant="subtitle1" align="justify" component="p">
            {convertNewlinesToBreaks(letter.generatedLetter || '')}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Close
        </Button>
        <Button
          onClick={handleCopy}
          variant="contained"
          color="primary"
          startIcon={<FileCopy />}
          sx={{
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: 'blue',
              transform: 'scale(1.05)',
            },
          }}
        >
          Copy
        </Button>
      </DialogActions>

      <Snackbar
        open={copied}
        autoHideDuration={4000}
        onClose={() => setCopied(false)}
      >
        <Alert
          onClose={() => setCopied(false)}
          severity="success"
          sx={{ width: '100%', maxWidth: 400 }}
        >
          Text copied! Always proofread your cover letter as AI can
          'hallucinate' and make mistakes.
        </Alert>
      </Snackbar>
    </Dialog>
  );
}
